import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  UserGroupIcon,
  UserPlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import CopyablePhone from '../ui/CopyablePhone';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import { getApiErrorMessage } from '../../utils/apiError';
import { prospectStatusLabel, PARTNER_STATUS_FALLBACK } from '../../utils/prospectStatus';

function VendorPartnersView() {
  const { t, formatMessage } = useLanguage();
  const navigate = useNavigate();
  const n = t.enrola?.partnerNetworkView || {};
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ partners: [], pending_prospects: [], counts: {} });

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/vendor/partners', { withCredentials: true });
      setData({
        partners: response.data?.partners || [],
        pending_prospects: response.data?.pending_prospects || [],
        counts: response.data?.counts || {},
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, t, formatMessage, n.failedToLoad));
    } finally {
      setLoading(false);
    }
  }, [formatMessage, n.failedToLoad, t]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const partnerStatuses = PARTNER_STATUS_FALLBACK;

  return (
    <div className="space-y-5 xl:space-y-6">
      <PageHeader
        eyebrow={n.eyebrow || 'Enrola'}
        title={n.title || 'Partner network'}
        subtitle={n.subtitle || 'Partners you have recruited and your pending partner prospects.'}
        icon={UserGroupIcon}
        actions={
          <Button
            variant="secondary"
            icon={UserPlusIcon}
            onClick={() => navigate('/vendor/prospects?create=1')}
          >
            {n.newPartner || 'New partner'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="card-premium p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">{n.partnersCount || 'Partners'}</p>
              <p className="text-3xl font-bold text-violet-600">{data.counts.partners || 0}</p>
            </div>
            <UserGroupIcon className="h-9 w-9 text-violet-500 opacity-40" />
          </div>
        </div>
        <div className="card-premium p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">{n.pendingCount || 'Pending prospects'}</p>
              <p className="text-3xl font-bold text-orange-600">{data.counts.pending_prospects || 0}</p>
            </div>
            <UserPlusIcon className="h-9 w-9 text-orange-500 opacity-40" />
          </div>
        </div>
      </div>

      {loading ? (
        <ListPageSkeleton rows={4} showHeader={false} />
      ) : (
        <>
          <div className="card-premium overflow-hidden">
            <div className="border-b border-secondary-200/60 p-4">
              <h3 className="text-base font-semibold text-secondary-900">
                {n.pendingTitle || 'Pending partner prospects'}
              </h3>
              <p className="text-sm text-secondary-500">
                {n.pendingSubtitle || 'Partner-program signups waiting to be enrolled.'}
              </p>
            </div>
            {data.pending_prospects.length === 0 ? (
              <EmptyState
                compact
                icon={UserPlusIcon}
                title={n.noPending || 'No pending partner prospects'}
                description={n.noPendingDesc || 'New partner signups will appear here.'}
                className="mx-4 my-6"
              />
            ) : (
              <div className="divide-y divide-secondary-100">
                {data.pending_prospects.map((prospect) => (
                  <div key={prospect.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-secondary-900">{prospect.name}</p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-secondary-500">
                        {prospect.email ? (
                          <span className="inline-flex items-center gap-1">
                            <EnvelopeIcon className="h-3.5 w-3.5" />
                            {prospect.email}
                          </span>
                        ) : null}
                        {prospect.phone ? (
                          <CopyablePhone value={prospect.phone} className="inline-flex items-center gap-1" />
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-700">
                        {prospectStatusLabel(prospect.status, t, partnerStatuses)}
                      </span>
                      <Button
                        size="small"
                        variant="secondary"
                        icon={ArrowRightIcon}
                        onClick={() => navigate('/vendor/prospects')}
                      >
                        {n.manage || 'Manage'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-premium overflow-hidden">
            <div className="border-b border-secondary-200/60 p-4">
              <h3 className="text-base font-semibold text-secondary-900">
                {n.partnersTitle || 'My partners'}
              </h3>
              <p className="text-sm text-secondary-500">
                {n.partnersSubtitle || 'Partners you have directly recruited.'}
              </p>
            </div>
            {data.partners.length === 0 ? (
              <EmptyState
                compact
                icon={UserGroupIcon}
                title={n.noPartners || 'No partners yet'}
                description={n.noPartnersDesc || 'Share your partner link to start building your network.'}
                className="mx-4 my-6"
              />
            ) : (
              <div className="divide-y divide-secondary-100">
                {data.partners.map((partner) => (
                  <div key={partner.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-secondary-900">{partner.username}</p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-secondary-500">
                        {partner.email ? (
                          <span className="inline-flex items-center gap-1">
                            <EnvelopeIcon className="h-3.5 w-3.5" />
                            {partner.email}
                          </span>
                        ) : null}
                        {(partner.phone_numbers || []).map((phone) => (
                          <span key={phone} className="inline-flex items-center gap-1">
                            <PhoneIcon className="h-3.5 w-3.5" />
                            {phone}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700 ring-1 ring-inset ring-violet-500/20">
                      {n.partnerBadge || 'Partner'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default VendorPartnersView;
